import { useMemo, useReducer, useState } from 'react';
import { keyBy } from 'lodash';
import { useMutation, useQuery } from '@apollo/client';
import { DasboardFarmAggregateQuery, DashboardQuery } from '../queries';
import { getUnusedBrightColor } from '../utils/colors';
import { UpsertMultiProjectDashboardMutation } from '../mutations';
import { mutateAndDisplayErrors } from '../utils/toast';

// Look through all farms in a dashboard and add any new crop colors to the color map
const getCropColorsFromFarms = (farms, colorMap = {}) => {
  const newColorMap = { ...colorMap };
  Object.values(farms).forEach((farm) => {
    farm.cropAggregates.forEach((cropAggregate) => {
      const cropName = cropAggregate.crop.name;
      if (!newColorMap[cropName]) {
        newColorMap[cropName] = cropAggregate.crop.color.id;
      }
    });
  });
  return newColorMap;
};

export const NEW_DASHBOARD_ID = 'NEW_DASHBOARD';

const initialState = {
  // Which user dashboard is currently displayed
  // NEW_DASHBOARD_ID indicates user is building a new dashboard
  activeDashboardId: NEW_DASHBOARD_ID,
  // Single empty dashboard
  dashboards: { [NEW_DASHBOARD_ID]: {} },
  // Selected farm -- used to display farm details in a card on the map
  selectedFarmId: null,
};

const dashboardStateReducer = (state, action) => {
  const checkFarmSelection = () => {
    const currentDashboard =
      state.dashboards[state.activeDashboardId]?.farms ?? {};
    const currentDashboardFarmIds = Object.values(currentDashboard).map(
      ({ id }) => id,
    );

    if (action.id != null && !currentDashboardFarmIds.includes(action.id)) {
      console.error(
        'Cannot select a farm that is not in the active dashboard.',
      );
      return false;
    }
    return true;
  };

  switch (action.type) {
    case 'bulkAdd': {
      return {
        ...state,
        dashboards: {
          // state.dashboards is an object, but action.dashboards is an array
          ...keyBy(action.dashboards, 'id'),
          // spread dashboards state last to avoid overwriting an existing dashboard
          ...state.dashboards,
        },
      };
    }
    case 'reset': {
      return initialState;
    }
    case 'setActiveDashboard': {
      return { ...state, activeDashboardId: action.id, selectedFarmId: null };
    }
    case 'removeDashboard': {
      if (action.id === NEW_DASHBOARD_ID) {
        console.error('Cannot remove the new dashboard');
        return state;
      }
      const newDashboards = { ...state.dashboards };
      delete newDashboards[action.id];
      return { ...state, dashboards: newDashboards };
    }
    case 'setSelectedFarm': {
      if (!checkFarmSelection()) return state;

      // select the specified farm
      return { ...state, selectedFarmId: action.id };
    }
    case 'toggleSelectedFarm': {
      if (!checkFarmSelection()) return state;

      // toggle the specified farm
      return {
        ...state,
        selectedFarmId: state.selectedFarmId === action.id ? null : action.id,
      };
    }
    case 'addFarm': {
      const { newFarm, updatedAt, cropColorMap } = action;

      const dashboard = state.dashboards[state.activeDashboardId];
      if (!dashboard) {
        console.error('Cannot update a dashboard that does not exist');
        return state;
      }

      const dashboardFarms = dashboard.farms || {};
      if (newFarm.id in dashboardFarms) {
        console.error('Cannot add farm that already exists in dashboard.');
        return state;
      }

      const farmColor = getUnusedBrightColor(
        Object.values(dashboardFarms).map(({ color }) => color),
      );

      const newDashboardFarms = {
        ...dashboardFarms,
        [newFarm.id]: { color: farmColor, ...newFarm },
      };

      return {
        ...state,
        dashboards: {
          ...state.dashboards,
          [state.activeDashboardId]: {
            ...dashboard,
            updatedAt,
            farms: newDashboardFarms,
            cropColorMap,
          },
        },
      };
    }
    case 'removeFarm': {
      const dashboard = state.dashboards[state.activeDashboardId];
      if (!dashboard) {
        console.error('Cannot update a dashboard that does not exist');
        return state;
      }

      const { farmId, updatedAt } = action;
      const newDashboardFarms = { ...dashboard.farms };
      delete newDashboardFarms[farmId];

      return {
        ...state,
        dashboards: {
          ...state.dashboards,
          [state.activeDashboardId]: {
            ...dashboard,
            updatedAt,
            farms: newDashboardFarms,
          },
        },
      };
    }
    case 'update': {
      return {
        ...state,
        dashboards: {
          ...state.dashboards,
          [action.dashboard.id || NEW_DASHBOARD_ID]: action.dashboard,
        },
      };
    }
    case 'updateAndSelect': {
      return {
        ...state,
        activeDashboardId: action.dashboard.id,
        dashboards: {
          ...state.dashboards,
          [action.dashboard.id]: action.dashboard,
        },
      };
    }
    default:
      throw new Error(`Invalid type: ${action.type}.`);
  }
};

// Should be accessed from useMultiProjectDashboardContext
const useDashboardState = () => {
  // STATE
  const [
    { activeDashboardId, selectedFarmId, dashboards },
    dispatch,
  ] = useReducer(dashboardStateReducer, initialState);

  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  // QUERIES
  // fetch additional farm data for a single farm added to an unsaved dashboard
  const { refetch: fetchAugmentedFarmData } = useQuery(
    DasboardFarmAggregateQuery,
    {
      skip: true,
    },
  );

  const { refetch: fetchDashboardData } = useQuery(DashboardQuery, {
    skip: true,
  });

  // MUTATIONS
  const [upsertDashboard] = useMutation(UpsertMultiProjectDashboardMutation);

  // UPDATES
  const setOwnedDashboardsData = (dashboardsResult) => {
    dispatch({
      type: 'bulkAdd',
      dashboards: dashboardsResult,
    });
  };

  const updateDashboardHelper = async (
    dispatchLocalChange,
    transformForRemote,
  ) => {
    const dashboard = dashboards[activeDashboardId];
    if (!dashboard) {
      console.error('Cannot update a dashboard that does not exist');
      return;
    }

    let updatedAt;
    if (activeDashboardId !== NEW_DASHBOARD_ID) {
      const newDashboardData = transformForRemote(dashboard);
      const result = await mutateAndDisplayErrors(() => {
        return upsertDashboard({
          variables: {
            input: {
              id: activeDashboardId,
              updatedAt: dashboard.updatedAt,
              ...newDashboardData,
            },
          },
        });
      });
      if (result) {
        updatedAt = result.updatedAt;
      } else {
        // Failed to update the dashboard
        // Display is handled by `mutateAndDisplayErrors`, so just return
        return;
      }
    }
    await dispatchLocalChange(dashboard, updatedAt);
  };

  const addDashboardFarm = async (farm) => {
    // When we add a new farm to the dashboard, we also want to save any new crop colors
    // So, first fetch and augment the farm data to get info about its crops
    const activeDashboard = dashboards[activeDashboardId];
    const {
      data: { farm: augmentedFarmData },
    } = await fetchAugmentedFarmData({ farmId: farm.id });
    const newFarm = { ...farm, ...augmentedFarmData };
    const cropColorMap = getCropColorsFromFarms(
      { ...activeDashboard.farms, [farm.id]: newFarm },
      activeDashboard.cropColorMap,
    );

    // Then, update the dashboard with the new farm and crop colors
    const transformForRemoteUpdate = (dashboard) => ({
      farmIds: [...Object.keys(dashboard.farms), farm.id],
      cropColorMap,
    });
    const dispatchLocalChange = async (_dashboard, newUpdatedAt) => {
      dispatch({
        type: 'addFarm',
        newFarm: { ...farm, ...augmentedFarmData },
        updatedAt: newUpdatedAt,
        cropColorMap,
      });
    };

    return updateDashboardHelper(dispatchLocalChange, transformForRemoteUpdate);
  };

  const removeDashboardFarm = async (farmId) => {
    const transformForRemoteUpdate = (dashboard) => {
      const newFarms = { ...dashboard.farms };
      delete newFarms[farmId];
      return { farmIds: Object.keys(newFarms) };
    };
    const dispatchLocalChange = (_dashboard, newUpdatedAt) =>
      dispatch({
        type: 'removeFarm',
        farmId,
        updatedAt: newUpdatedAt,
      });

    return updateDashboardHelper(dispatchLocalChange, transformForRemoteUpdate);
  };

  const clearDashboardFarms = () => {
    const dashboard = dashboards[activeDashboardId];
    if (!dashboard) {
      console.error('Cannot update a dashboard that does not exist');
      return;
    }
    dispatch({
      type: 'update',
      dashboard: {
        ...dashboard,
        farms: {},
      },
    });
  };

  const clearDashboard = (id) => {
    const dashboard = dashboards[id];
    if (!dashboard) {
      console.error('Cannot update a dashboard that does not exist');
      return;
    }
    dispatch({
      type: 'update',
      dashboard: {
        id,
      },
    });
  };

  const resetDashboards = () => dispatch({ type: 'reset' });

  const setDashboardName = async (name) => {
    const transformForRemoteUpdate = () => ({ name });
    const dispatchLocalChange = (dashboard, newUpdatedAt) =>
      dispatch({
        type: 'update',
        dashboard: {
          ...dashboard,
          updatedAt: newUpdatedAt,
          name,
        },
      });

    return updateDashboardHelper(dispatchLocalChange, transformForRemoteUpdate);
  };

  const setCropColor = async (cropName, colorId) => {
    const transformForRemoteUpdate = (dashboard) => {
      const cropColorMap = { ...dashboard.cropColorMap, [cropName]: colorId };
      return { cropColorMap };
    };
    const dispatchLocalChange = (dashboard, newUpdatedAt) => {
      const cropColorMap = { ...dashboard.cropColorMap, [cropName]: colorId };
      dispatch({
        type: 'update',
        dashboard: {
          ...dashboard,
          updatedAt: newUpdatedAt,
          cropColorMap,
        },
      });
    };

    return updateDashboardHelper(dispatchLocalChange, transformForRemoteUpdate);
  };

  const loadAndSelectDashboard = async (id) => {
    if (id == null || id === NEW_DASHBOARD_ID) {
      console.error('Cannot load data for the new dashboard');
      return;
    }

    if (id === activeDashboardId) {
      return;
    }

    try {
      // If the dashboard has not been loaded yet, fetch it and select it
      if (!dashboards[id]?.farms) {
        setDashboardLoading(true);
        const { data } = await fetchDashboardData({ id });

        const usedColors = [];
        const augmentedFarms = data?.multiProjectDashboard?.farms.map(
          (farm) => {
            const farmColor = getUnusedBrightColor(usedColors);
            usedColors.push(farmColor);
            return { ...farm, color: farmColor };
          },
        );
        dispatch({
          type: 'updateAndSelect',
          dashboard: {
            id,
            ...data?.multiProjectDashboard,
            farms: keyBy(augmentedFarms, 'id'),
          },
        });
      } else {
        // If it has been loaded before, just select it
        dispatch({ type: 'setActiveDashboard', id });
      }
    } catch (e) {
      console.error('Error fetching dashboard data:', e);
      setDashboardError(e);
    } finally {
      setDashboardLoading(false);
    }
  };

  const selectDashboard = async (dashboardId) => {
    if (dashboardId === activeDashboardId) {
      return;
    }
    dispatch({ type: 'setActiveDashboard', id: dashboardId });
  };

  const removeDashboard = (dashboardId) => {
    dispatch({ type: 'removeDashboard', id: dashboardId });
  };

  const selectFarm = (farmId) =>
    dispatch({ type: 'setSelectedFarm', id: farmId });

  const toggleFarm = (farmId) =>
    dispatch({ type: 'toggleSelectedFarm', id: farmId });

  const dashboardsArray = useMemo(() => Object.values(dashboards), [
    dashboards,
  ]);

  const dashboardFarmsMap = useMemo(
    () => dashboards[activeDashboardId]?.farms || {},
    [dashboards, activeDashboardId],
  );

  const dashboardFarms = useMemo(() => Object.values(dashboardFarmsMap), [
    dashboardFarmsMap,
  ]);

  return {
    setOwnedDashboardsData,
    dashboardLoading,
    dashboardError,
    setDashboardError,
    activeDashboardId,
    activeDashboard: dashboards[activeDashboardId],
    selectDashboard,
    loadAndSelectDashboard,
    removeDashboard,
    selectedFarmId,
    dashboards: dashboardsArray,
    dashboardFarmsMap,
    dashboardFarms,
    // addDashboards, // Add dashboards
    addDashboardFarm, // Add a farm to active dashboard
    // addDashboardFarms, // Bulk add farms to a dashboard
    removeDashboardFarm, // Remove a farm from active dashboard by id
    setCropColor, // Set the color for a crop in the active dashboard by id
    setDashboardName, // Set the name for the active dashboard
    clearDashboardFarms, // Clear farms in active dashboard
    clearDashboard, // Clear a dashboard by id
    resetDashboards, // Reset dashboards to initial state
    selectFarm, // Select a farm to display details (null to clear)
    toggleFarm, // Toggle a farm's selection
  };
};

export default useDashboardState;
